import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { toastErrorIfNotForbidden } from "@/shared/utils/errorHandling";
import { SUGGESTIONS_MESSAGES } from "../configs/constants";
import { SUGGESTIONS_QUERY_KEYS } from "../configs/queryKeys";
import { SuggestionsService } from "../services/suggestions.service";
import type {
  CreateUserSuggestionPayload,
  SuggestionsQueryParams,
  UpdateUserSuggestionPayload,
} from "../types/suggestions.types";

/**
 * Hook to fetch the list of suggestions
 */
export const useSuggestions = (params?: SuggestionsQueryParams, enabled = true) =>
  useQuery({
    queryKey: SUGGESTIONS_QUERY_KEYS.list(params),
    queryFn: () => SuggestionsService.listSuggestions(params),
    enabled,
  });

/**
 * Hook to create a new suggestion
 */
export const useCreateSuggestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateUserSuggestionPayload) =>
      SuggestionsService.createSuggestion(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEYS.all });
      toast.success(SUGGESTIONS_MESSAGES.CREATE_SUCCESS);
    },
    onError: (error) => {
      toastErrorIfNotForbidden(error, SUGGESTIONS_MESSAGES.CREATE_ERROR);
    },
  });
};

/**
 * Hook to update an existing suggestion
 */
export const useUpdateSuggestion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string
      payload: UpdateUserSuggestionPayload
    }) => SuggestionsService.updateSuggestion(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: SUGGESTIONS_QUERY_KEYS.detail(variables.id) });
      toast.success(SUGGESTIONS_MESSAGES.UPDATE_SUCCESS);
    },
    onError: (error) => {
      toastErrorIfNotForbidden(error, SUGGESTIONS_MESSAGES.UPDATE_ERROR);
    },
  });
};
